/**
 * Auth context for the Outlook task pane.
 *
 * Primary path: Microsoft NAA/MSAL -> backend exchange -> Firebase custom token.
 * Fallback path: Firebase email/password for dev or unsupported Outlook hosts.
 * Outlook never self-provisions portal profiles; activation owns tenant binding.
 */
import {
  browserLocalPersistence,
  type User as FirebaseUser,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  setPersistence,
  signInWithCustomToken,
  signInWithEmailAndPassword,
} from 'firebase/auth';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { authErrorMessage } from './authErrors';
import { getFirebaseAuth } from './firebase';
import { waitForFirebaseUser } from './firebaseAuthState';
import { logger } from './logger';
import { exchangeMicrosoftIdentityForCustomToken, mailboxMatchesEmail } from './microsoftAuth';

export interface AuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
}

interface AuthContextValue {
  user: AuthUser | null;
  /** True until the first onAuthStateChanged fires (restoring any persisted session). */
  loading: boolean;
  /** Last sign-in error message, cleared on the next attempt. */
  error: string | null;
  signInWithMicrosoft: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  // Sends a password-reset email. Also the way a user "claims" a passwordless
  // account auto-created by the Gmail add-on (createUser without a password):
  // the reset link lets them set a first password, after which signIn works.
  sendPasswordReset: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
  getIdToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const toAuthUser = (fb: FirebaseUser | null): AuthUser | null =>
  fb ? { uid: fb.uid, email: fb.email, displayName: fb.displayName } : null;

const TRANSIENT_AUTH_NULL_GRACE_MS = 10_000;
const AUTH_SEEN_STORAGE_KEY = 'aira-outlook-auth-seen';

function rememberSignedInUser(user: AuthUser | null): void {
  try {
    if (user) {
      window.localStorage.setItem(
        AUTH_SEEN_STORAGE_KEY,
        JSON.stringify({ uid: user.uid, email: user.email, at: Date.now() }),
      );
    } else {
      window.localStorage.removeItem(AUTH_SEEN_STORAGE_KEY);
    }
  } catch {
    /* localStorage can be unavailable in hardened hosts. */
  }
}

function hasRecentSignedInUser(): boolean {
  try {
    const raw = window.localStorage.getItem(AUTH_SEEN_STORAGE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { at?: unknown };
    return typeof parsed.at === 'number' && Date.now() - parsed.at < 7 * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const userRef = useRef<AuthUser | null>(null);
  const clearingTimerRef = useRef<number | null>(null);
  const explicitSignOutRef = useRef(false);
  const restoreInFlightRef = useRef(false);

  const applyUser = useCallback((next: AuthUser | null) => {
    userRef.current = next;
    setUser(next);
    rememberSignedInUser(next);
  }, []);

  const restoreMicrosoftSession = useCallback(async () => {
    if (restoreInFlightRef.current) return;
    restoreInFlightRef.current = true;
    const preserveMountedSession = Boolean(userRef.current);
    if (!preserveMountedSession) setLoading(true);
    setError(null);
    try {
      const auth = getFirebaseAuth();
      await setPersistence(auth, browserLocalPersistence);
      const { customToken } = await exchangeMicrosoftIdentityForCustomToken({
        allowInteractive: false,
      });
      await signInWithCustomToken(auth, customToken);
    } catch (e) {
      logger.warn('[auth] silent Microsoft session restore failed', { err: e });
      applyUser(null);
    } finally {
      restoreInFlightRef.current = false;
      if (!preserveMountedSession) setLoading(false);
    }
  }, [applyUser]);

  useEffect(() => {
    let unsub: (() => void) | undefined;
    try {
      const auth = getFirebaseAuth();
      void setPersistence(auth, browserLocalPersistence).catch((err) => {
        logger.warn('[auth] could not set local persistence', { err });
      });
      unsub = onAuthStateChanged(auth, (fb) => {
        if (clearingTimerRef.current !== null) {
          window.clearTimeout(clearingTimerRef.current);
          clearingTimerRef.current = null;
        }
        if (fb) {
          if (!mailboxMatchesEmail(fb.email)) {
            logger.warn('[auth] persisted AIRA identity does not match the active Outlook mailbox');
            setError(
              'The saved AIRA account does not match this Outlook mailbox. Sign in with the mailbox account.',
            );
            applyUser(null);
            void firebaseSignOut(auth);
            setLoading(false);
            return;
          }
          explicitSignOutRef.current = false;
          applyUser(toAuthUser(fb));
          setLoading(false);
          return;
        }
        if (explicitSignOutRef.current) {
          applyUser(null);
          setLoading(false);
          return;
        }
        if (!userRef.current && hasRecentSignedInUser()) {
          void restoreMicrosoftSession();
          return;
        }
        if (!userRef.current) {
          applyUser(null);
          setLoading(false);
          return;
        }
        logger.warn('[auth] transient empty Firebase user; keeping taskpane session briefly');
        setLoading(false);
        void restoreMicrosoftSession();
        clearingTimerRef.current = window.setTimeout(() => {
          clearingTimerRef.current = null;
          if (!getFirebaseAuth().currentUser) {
            if (hasRecentSignedInUser()) {
              void restoreMicrosoftSession();
            } else {
              applyUser(null);
            }
          }
        }, TRANSIENT_AUTH_NULL_GRACE_MS);
      });
    } catch (e) {
      // Missing/invalid IdP config — surface it instead of hanging on a spinner.
      setError((e as Error).message);
      setLoading(false);
    }
    return () => {
      unsub?.();
      if (clearingTimerRef.current !== null) {
        window.clearTimeout(clearingTimerRef.current);
        clearingTimerRef.current = null;
      }
    };
  }, [applyUser, restoreMicrosoftSession]);

  const signIn = useCallback(async (email: string, password: string) => {
    setError(null);
    const auth = getFirebaseAuth();
    // Persist across task-pane reloads (Office recreates the pane frequently).
    await setPersistence(auth, browserLocalPersistence);
    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
    } catch (e) {
      setError(authErrorMessage(e, 'Sign-in failed. Please try again.'));
      throw e;
    }
  }, []);

  const signInWithMicrosoft = useCallback(async () => {
    setError(null);
    const auth = getFirebaseAuth();
    await setPersistence(auth, browserLocalPersistence);
    try {
      const { customToken } = await exchangeMicrosoftIdentityForCustomToken();
      await signInWithCustomToken(auth, customToken);
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : authErrorMessage(e, 'Microsoft sign-in failed. Please try again.'),
      );
      throw e;
    }
  }, []);

  const sendPasswordReset = useCallback(async (email: string) => {
    setError(null);
    try {
      await sendPasswordResetEmail(getFirebaseAuth(), email.trim());
    } catch (e) {
      setError(authErrorMessage(e, 'Could not send the reset email. Please try again.'));
      throw e;
    }
  }, []);

  const signOut = useCallback(async () => {
    explicitSignOutRef.current = true;
    if (clearingTimerRef.current !== null) {
      window.clearTimeout(clearingTimerRef.current);
      clearingTimerRef.current = null;
    }
    rememberSignedInUser(null);
    applyUser(null);
    await firebaseSignOut(getFirebaseAuth());
  }, [applyUser]);

  const getIdToken = useCallback(async () => {
    const auth = getFirebaseAuth();
    const u = auth.currentUser ?? (userRef.current ? await waitForFirebaseUser(auth) : null);
    return u ? u.getIdToken() : null;
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      error,
      signInWithMicrosoft,
      signIn,
      sendPasswordReset,
      signOut,
      getIdToken,
    }),
    [user, loading, error, signInWithMicrosoft, signIn, sendPasswordReset, signOut, getIdToken],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
